import React from 'react';
import {
  ArrowLeftOutlined,
  BankOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  FileSearchOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { Park } from '../../types';
import {
  PARK_PROFILES,
  type ParkProfileFactor,
} from '../../constants/parkProfiles';

interface ParkProfilePageProps {
  park: Park;
  onBack: () => void;
}

const FACTOR_ICONS = [
  <BulbOutlined key="energy" />,
  <BankOutlined key="industry" />,
  <ThunderboltOutlined key="power" />,
  <GlobalOutlined key="global" />,
  <CheckCircleOutlined key="ready" />,
];

function factorStateClass(state: ParkProfileFactor['state']): string {
  if (state === '已接入') return 'park-profile-state park-profile-state--ready';
  if (state === '部分接入') return 'park-profile-state park-profile-state--partial';
  return 'park-profile-state park-profile-state--pending';
}

export function ParkProfilePage(props: ParkProfilePageProps): React.ReactElement {
  const { park, onBack } = props;
  const profile = PARK_PROFILES[park.name];

  if (!profile) {
    return (
      <div className="park-profile-page park-profile-page--empty">
        <button type="button" className="park-profile-back" onClick={onBack}>
          <ArrowLeftOutlined /> 返回园区管理
        </button>
        <div>该园区尚未配置专题详情。</div>
      </div>
    );
  }

  return (
    <div className="park-profile-page">
      <header className="park-profile-topbar">
        <div className="park-profile-brand">
          <span className="park-profile-brand__mark">ZC</span>
          零碳园区数字化平台
        </div>
        <div className="park-profile-nav">
          <span>园区查询</span>
          <span>政策查询</span>
          <span>工作管理</span>
          <span className="park-profile-nav__active">园区专题</span>
        </div>
        <button type="button" className="park-profile-back" onClick={onBack}>
          <ArrowLeftOutlined /> 返回地图
        </button>
      </header>

      <main className="park-profile-main">
        <div className="park-profile-breadcrumb">
          园区管理 / {park.province} / {profile.displayName}
        </div>
        <div className="park-profile-heading">
          <div>
            <h1>{profile.displayName}</h1>
            <p>{profile.subtitle}　｜　{profile.levelLabel}</p>
          </div>
          <div className="park-profile-heading__badges">
            <span>专题档案</span>
            <span>{park.dockingStatus || '未对接'}</span>
          </div>
        </div>

        <section className="park-profile-hero-grid">
          <article className="park-profile-panel">
            <div className="park-profile-panel__head">
              <EnvironmentOutlined />
              <h2>园区范围与空间边界</h2>
              <span>规划范围示意</span>
            </div>
            <div className="park-profile-map">
              {profile.range.imageUrl ? (
                <img
                  className="park-profile-map__image"
                  src={profile.range.imageUrl}
                  alt={`${profile.displayName}园区申报范围示意图`}
                />
              ) : (
                <>
                  <div className="park-profile-map__grid" />
                  <div className="park-profile-map__road park-profile-map__road--a" />
                  <div className="park-profile-map__road park-profile-map__road--b" />
                  <div className="park-profile-map__road park-profile-map__road--c" />
                  <div className="park-profile-map__boundary" />
                  {profile.range.zones.map((zone, index) => (
                    <span key={zone} className={`park-profile-map__zone park-profile-map__zone--${index + 1}`}>
                      {zone}
                    </span>
                  ))}
                  <div className="park-profile-map__notice">{profile.range.boundaryStatus}</div>
                </>
              )}
            </div>
            <div className="park-profile-range-summary">
              <div><strong>{profile.range.primaryValue}</strong><span>{profile.range.primaryLabel}</span></div>
              <div><strong>{profile.range.secondaryValue}</strong><span>{profile.range.secondaryLabel}</span></div>
              <div><strong>{profile.range.mode}</strong><span>{profile.range.location}</span></div>
            </div>
          </article>

          <article className="park-profile-panel">
            <div className="park-profile-panel__head">
              <FileSearchOutlined />
              <h2>零碳园区申报关注因素</h2>
              <span>五维信息视图</span>
            </div>
            <div className="park-profile-factors">
              {profile.factors.map((factor, index) => (
                <div className="park-profile-factor" key={factor.label}>
                  <div className="park-profile-factor__icon">{FACTOR_ICONS[index]}</div>
                  <div>
                    <strong>{factor.label}</strong>
                    <p>{factor.description}</p>
                  </div>
                  <span className={factorStateClass(factor.state)}>{factor.state}</span>
                </div>
              ))}
            </div>
          </article>
        </section>

        <section className="park-profile-metrics">
          {profile.metrics.map((metric) => (
            <div className="park-profile-metric" key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
              <small>{metric.note}</small>
            </div>
          ))}
        </section>

        <section className="park-profile-content-grid">
          <article className="park-profile-panel">
            <div className="park-profile-panel__head"><ThunderboltOutlined /><h2>能源与电力条件</h2><span>资源—负荷—网架</span></div>
            <div className="park-profile-rows">
              {profile.energyRows.map((row) => (
                <div className="park-profile-row" key={row.label}><span>{row.label}</span><p>{row.value}</p></div>
              ))}
            </div>
          </article>

          <article className="park-profile-panel">
            <div className="park-profile-panel__head"><BankOutlined /><h2>产业基础与减碳需求</h2><span>产业链—企业—外向度</span></div>
            <div className="park-profile-rows">
              <div className="park-profile-row"><span>主导产业</span><div className="park-profile-tags">{profile.industries.map((item) => <em key={item}>{item}</em>)}</div></div>
              <div className="park-profile-row"><span>重点企业</span><p>{profile.keyEnterprises}</p></div>
              <div className="park-profile-row"><span>减碳驱动</span><p>{profile.reductionDrivers}</p></div>
              <div className="park-profile-row"><span>改造方向</span><p>{profile.transformationDirections}</p></div>
            </div>
          </article>

          <article className="park-profile-panel">
            <div className="park-profile-panel__head"><CheckCircleOutlined /><h2>基础优势</h2><span>现有条件</span></div>
            <ul className="park-profile-list park-profile-list--positive">
              {profile.strengths.map((item) => <li key={item}><CheckCircleOutlined />{item}</li>)}
            </ul>
          </article>

          <article className="park-profile-panel">
            <div className="park-profile-panel__head"><InfoCircleOutlined /><h2>资料缺口与建设约束</h2><span>待推进事项</span></div>
            <ul className="park-profile-list park-profile-list--pending">
              {profile.constraints.map((item) => <li key={item}><InfoCircleOutlined />{item}</li>)}
            </ul>
          </article>
        </section>

        <section className="park-profile-sources">
          <FileSearchOutlined />
          <div><strong>信息来源与口径</strong><p>{profile.sources.join('；')}。正式数值保留年份与统计口径；缺失字段显示“待收资”。</p></div>
        </section>
      </main>
    </div>
  );
}
